import dayjs from 'dayjs';
import _capitalize from 'lodash/capitalize';
import {
  FULFILLMENT_STATUS,
  MAINTENANCE_CALENDAR_MODES,
  MAINTENANCE_FREQUENCY_TYPES,
  MAINTENANCE_SCOPE,
} from 'src/constants/maintenance';
import { API_RESOURCES, WORK_INSTRUCTION_TYPES } from 'src/utils/constants';
import {
  isWorkInstructionValidByThreshold,
  WORK_INSTRUCTION_THRESHOLD_ACTIONS,
} from 'src/utils/thresholds';
import { getResourceUri, getUuid } from 'src/utils/url';

/**
 * Helpers for the Machine Maintenance operator flow. Presentation-only logic
 * (summaries, scope, answer serialization) lives here so components stay lean.
 */

/* ── Frequency summary — "Every 100 runs", "Every 30 days", "Every other Monday" ── */
const pluralUnit = (interval, unit) => (interval === 1 ? unit.replace(/s$/, '') : unit);

export const summarizeFrequency = requirement => {
  const type = requirement?.frequency_type;
  const config = requirement?.frequency_config || {};

  if (type === MAINTENANCE_FREQUENCY_TYPES.USAGE_COUNT) {
    return `Every ${config.threshold} runs`;
  }

  if (type === MAINTENANCE_FREQUENCY_TYPES.CALENDAR) {
    const interval = Number(config.interval) || 1;
    const unit = config.unit || 'days';

    if (config.mode === MAINTENANCE_CALENDAR_MODES.FIXED) {
      const weekday = (config.weekdays || [])[0];
      if (unit === 'weeks' && weekday) {
        const label = _capitalize(weekday);
        if (interval === 1) return `Every ${label}`;
        if (interval === 2) return `Every other ${label}`;
        return `Every ${interval} weeks on ${label}`;
      }
      if (interval === 1) return `Every ${pluralUnit(1, unit)}`;
      return `Every ${interval} ${unit}`;
    }

    // relative
    if (interval === 1) return `Every ${pluralUnit(1, unit)}`;
    return `Every ${interval} ${unit}`;
  }

  return '';
};

/* ── Scope: instance vs type, derived (no stored scope field) ── */
export const deriveScope = (requirement, instanceTargetUri) => {
  if (!requirement?.target_uri || !instanceTargetUri) return MAINTENANCE_SCOPE.TYPE;
  return requirement.target_uri === instanceTargetUri
    ? MAINTENANCE_SCOPE.INSTANCE
    : MAINTENANCE_SCOPE.TYPE;
};

/* ── Requirement checklist instructions (ordered) ── */
export const getRequirementInstructions = requirement => {
  const instructions = requirement?.work_checklist?.work_instructions || [];
  return [...instructions]
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
    .map(instruction => ({
      uuid: instruction.uuid,
      // Nested checklist instructions carry uuid but not always uri — build it
      // (matches the run-flow pattern) so report POSTs reference a valid URI.
      uri:
        instruction.uri ||
        (instruction.uuid
          ? getResourceUri(API_RESOURCES.WORK_INSTRUCTION, instruction.uuid)
          : null),
      position: instruction.position ?? 0,
      question: instruction.description || instruction.name || '',
      reportType: instruction.report_type,
      required: Boolean(instruction.required),
      choices: instruction.choices || [],
      reportUnits: instruction.report_units || null,
      threshold: instruction.threshold || null,
      thresholdType: instruction.threshold_type || null,
      thresholdAction: instruction.threshold_action || null,
      additionalInstructionUrl: instruction.additional_instruction_url || null,
      parserTemplate: instruction.parser_template || null,
    }));
};

export const getStepCount = requirement => getRequirementInstructions(requirement).length;

/* ── Answer serialization for POST /maintenance-instruction-report/ ──
 * Mirrors the backend validators: text/number/dropdown/output-at-location send
 * the raw string; checkbox -> "true"/"false"; range -> JSON "[min,max]";
 * no-entry sends no report (an acknowledge record); picture/document upload a
 * document and PUT its uuid separately (handled in the write layer). */
const RAW_REPORT_TYPES = [
  WORK_INSTRUCTION_TYPES.TEXT,
  WORK_INSTRUCTION_TYPES.NUMBER,
  WORK_INSTRUCTION_TYPES.SINGLE_SELECT_DROPDOWN,
  WORK_INSTRUCTION_TYPES.OUTPUT_AT_LOCATION,
];

export const serializeReportValue = (reportType, value) => {
  if (reportType === WORK_INSTRUCTION_TYPES.NO_ENTRY) return undefined;
  if (RAW_REPORT_TYPES.includes(reportType)) return value == null ? '' : String(value);
  // checkbox (bool) -> "true"/"false"; range (array) -> "[min,max]"
  return JSON.stringify(value);
};

export const parseReportValue = (reportType, report) => {
  if (report == null || report === '') return null;
  if (reportType === WORK_INSTRUCTION_TYPES.CHECKBOX) return report === 'true' || report === true;
  if (reportType === WORK_INSTRUCTION_TYPES.RANGE) {
    try {
      const parsed = typeof report === 'string' ? JSON.parse(report) : report;
      return Array.isArray(parsed) ? parsed : ['', ''];
    } catch (_) {
      return ['', ''];
    }
  }
  return report;
};

/**
 * The parsed answer value for a captured report row: an acknowledge (no-entry)
 * report stores no value, so its existence means "true"; every other type is
 * parsed by report type. File-bearing answers keep their file separately -
 * callers seeding a form use the file itself as the value.
 */
export const parseReportRowValue = (reportType, report) => {
  if (!report) return null;
  if (reportType === WORK_INSTRUCTION_TYPES.NO_ENTRY) return true;
  return parseReportValue(reportType, report.report);
};

/* ── Answer completeness (drives progress + requirement auto-complete) ── */
export const isInstructionAnswered = (reportType, value) => {
  switch (reportType) {
    case WORK_INSTRUCTION_TYPES.NO_ENTRY:
      return value === true;
    case WORK_INSTRUCTION_TYPES.CHECKBOX:
      return value === true || value === false;
    case WORK_INSTRUCTION_TYPES.RANGE:
      return (
        Array.isArray(value) &&
        value[0] !== '' &&
        value[0] != null &&
        value[1] !== '' &&
        value[1] != null
      );
    case WORK_INSTRUCTION_TYPES.PICTURE:
    case WORK_INSTRUCTION_TYPES.DOCUMENT:
      return value != null && value !== '';
    default:
      return value != null && value !== '';
  }
};

/** A requirement is completable once every *required* instruction is answered. */
export const areRequiredInstructionsAnswered = (instructions, valuesByUuid) =>
  instructions
    .filter(instruction => instruction.required)
    .every(instruction =>
      isInstructionAnswered(instruction.reportType, valuesByUuid[instruction.uuid])
    );

/* A half-entered range: exactly one bound filled. Empty is a valid "left
 * unanswered", complete is an answer — but a half pair would be recorded as
 * nothing and read back as "None", so it must be resolved either way. */
export const isRangePartiallyFilled = (reportType, value) => {
  if (reportType !== WORK_INSTRUCTION_TYPES.RANGE || !Array.isArray(value)) return false;
  return value.filter(bound => bound !== '' && bound != null).length === 1;
};

export const hasPartiallyFilledRange = (instructions, valuesByUuid) =>
  instructions.some(instruction =>
    isRangePartiallyFilled(instruction.reportType, valuesByUuid[instruction.uuid])
  );

/* Mirrors the runs rule: an Error-action value outside the threshold still
 * saves, but the step is not done and the event cannot complete until it is
 * fixed. Empty values and Warning actions never block. */
export const isThresholdErrorBlocking = (instruction, value) =>
  Boolean(
    instruction.threshold &&
    instruction.thresholdAction === WORK_INSTRUCTION_THRESHOLD_ACTIONS.ERROR &&
    isInstructionAnswered(instruction.reportType, value) &&
    !isWorkInstructionValidByThreshold(value, instruction.threshold, instruction.thresholdType)
  );

export const hasBlockingThresholdErrors = (instructions, valuesByUuid) =>
  instructions.some(instruction =>
    isThresholdErrorBlocking(instruction, valuesByUuid[instruction.uuid])
  );

/* ── Human length of a downtime window — "45 min", "1h 30min", "2 days", "42d 1h" ── */
export const summarizeWindowDuration = (start, finish) => {
  if (!start || !finish) return '';
  const minutes = dayjs(finish).diff(dayjs(start), 'minute');
  if (minutes <= 0) return '';
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  if (days > 0) return hours ? `${days}d ${hours}h` : `${days} ${days === 1 ? 'day' : 'days'}`;
  if (hours > 0)
    return mins ? `${hours}h ${mins}min` : `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  return `${mins} min`;
};

/**
 * Events carry no name — label them "Maintenance Event N", numbered oldest-first
 * by the best available timestamp with uri as a stable tiebreaker (exact parity
 * with rapidfab's numbering so both products show the same names).
 */
export const buildEventNamesByUri = (events, downtimesByUri = {}) => {
  const sortKey = event =>
    event.started_at ||
    event.completed_at ||
    (event.downtime && downtimesByUri[event.downtime]?.start) ||
    '';
  const namesByUri = {};
  [...(events || [])]
    .sort((a, b) => {
      if (sortKey(a) !== sortKey(b)) return sortKey(a) < sortKey(b) ? -1 : 1;
      return a.uri < b.uri ? -1 : 1;
    })
    .forEach((event, index) => {
      namesByUri[event.uri] = `Maintenance Event ${index + 1}`;
    });
  return namesByUri;
};

/** True when the event ran past its planned downtime window (late completion). */
export const isPastScheduledFinish = (scheduledFinish, now = dayjs()) =>
  Boolean(scheduledFinish) && dayjs(now).isAfter(dayjs(scheduledFinish));

/* ── Last-performed history, keyed by requirement ROOT uuid ──────────────────
 * Fulfillments pin a specific revision; grouping by `root_requirement || uri`
 * keeps the history when a requirement is revised (rapidfab parity —
 * useMaintenanceEvents' buildUsageByRoot). Latest `completed_at` among
 * fulfillments that actually completed wins. */
export const buildLastPerformedByRoot = (fulfillments, requirementsByUri = {}) => {
  const lastByRoot = {};
  (fulfillments || []).forEach(fulfillment => {
    if (fulfillment.status !== FULFILLMENT_STATUS.COMPLETE || !fulfillment.completed_at) return;
    const pinned = requirementsByUri[fulfillment.maintenance_requirement];
    if (!pinned) return;
    const rootUuid = getUuid(pinned.root_requirement || pinned.uri);
    if (!rootUuid) return;
    if (!lastByRoot[rootUuid] || dayjs(fulfillment.completed_at).isAfter(lastByRoot[rootUuid])) {
      lastByRoot[rootUuid] = fulfillment.completed_at;
    }
  });
  return lastByRoot;
};

export const getRequirementRootUuid = requirement =>
  requirement ? getUuid(requirement.root_requirement || requirement.uri) : null;

export const buildDefaultDowntimeForm = description => {
  const start = dayjs();
  const finish = start.add(1, 'hour');
  return {
    description,
    startDate: start.format('YYYY-MM-DD'),
    startTime: start.format('HH:mm'),
    finishDate: finish.format('YYYY-MM-DD'),
    finishTime: finish.format('HH:mm'),
  };
};

const parseLocalDateTime = (date, time) => dayjs(`${date}T${time || '00:00'}`);

export const validateDowntimeForm = form => {
  if (!form.description?.trim()) return 'Description is required.';
  const start = parseLocalDateTime(form.startDate, form.startTime);
  const finish = parseLocalDateTime(form.finishDate, form.finishTime);
  if (!start.isValid() || !finish.isValid()) return 'Start and finish must be valid dates.';
  if (!finish.isAfter(start)) return 'Finish must be after start.';
  return null;
};

export const serializeDowntimeForm = form => ({
  description: form.description.trim(),
  start: parseLocalDateTime(form.startDate, form.startTime).toISOString(),
  finish: parseLocalDateTime(form.finishDate, form.finishTime).toISOString(),
});

export const reportMatchesInstruction = (report, instruction) => {
  if (!report?.work_instruction || !instruction) return false;
  const reportInstruction = report.work_instruction;
  return (
    reportInstruction === instruction.uri ||
    reportInstruction === instruction.uuid ||
    getUuid(reportInstruction) === instruction.uuid
  );
};

export const instructionHasReport = (reports = [], instruction) =>
  reports.some(report => reportMatchesInstruction(report, instruction));

export const normalizeReportFile = file => {
  if (!file) return null;
  return typeof file === 'string' ? { uri: file } : file;
};

/** Group instruction report rows by their fulfillment uri. */
export const groupReportsByFulfillment = (reports = []) =>
  (reports || []).reduce((byFulfillment, report) => {
    const fulfillmentUri = report.maintenance_event_requirement_fulfillment;
    if (!byFulfillment[fulfillmentUri]) byFulfillment[fulfillmentUri] = [];
    byFulfillment[fulfillmentUri].push(report);
    return byFulfillment;
  }, {});

/**
 * Insert `resource` into a `{uri}`-keyed list, or shallow-merge it onto the
 * matching entry. New resources are prepended; a resource without a uri leaves
 * the list untouched.
 */
export const upsertByUri = (resources, resource) => {
  if (!resource?.uri) return resources;
  const exists = resources.some(item => item.uri === resource.uri);
  return exists
    ? resources.map(item => (item.uri === resource.uri ? { ...item, ...resource } : item))
    : [resource, ...resources];
};

/**
 * Diff selected requirement uris against an event's current fulfillments:
 * `toAdd` = requirement uris needing a new fulfillment, `toRemove` = existing
 * fulfillments whose requirement is no longer selected.
 */
export const computeFulfillmentDiff = (fulfillments, selectedUris) => {
  const existingRequirementUris = new Set(
    fulfillments.map(fulfillment => fulfillment.maintenance_requirement)
  );
  const selected = new Set(selectedUris);
  return {
    toAdd: selectedUris.filter(uri => !existingRequirementUris.has(uri)),
    toRemove: fulfillments.filter(
      fulfillment => !selected.has(fulfillment.maintenance_requirement)
    ),
  };
};

/**
 * Operator-facing copy for the flow's terminal success screen, derived from the
 * `successState` snapshot ({ type, wasPlanned, hadDowntime, freeNow }).
 */
export const getSuccessMessage = successState => {
  if (successState.type === 'planned') {
    return 'Saved as a planned event. Start it from the list when the downtime window arrives.';
  }
  const machineNote = successState.hadDowntime
    ? successState.freeNow
      ? ' The machine has been freed.'
      : ' The machine stays blocked until its planned end.'
    : '';
  if (successState.type !== 'cancelled') {
    return `Logged on this machine.${machineNote} Find it under Activity.`;
  }
  if (successState.wasPlanned) {
    return 'The planned event was removed. Nothing was recorded.';
  }
  return `No completions were recorded. Any open requirements were skipped.${machineNote}`;
};

const SEVERITY_ORDER = {
  overdue: 3,
  warning: 2,
  healthy: 1,
};

const progressByFrequencyType = {
  [MAINTENANCE_FREQUENCY_TYPES.USAGE_COUNT]: due => {
    if (due.usage_count_since_last == null || due.usage_count_threshold == null) {
      return Infinity;
    }

    return due.usage_count_threshold - due.usage_count_since_last;
  },

  [MAINTENANCE_FREQUENCY_TYPES.CALENDAR]: due =>
    new Date(due.transition_at || due.next_due_at || 0).getTime(),
};

const getProgress = due => {
  if (!due) return Infinity;
  return progressByFrequencyType[due.frequency_type]?.(due) ?? Infinity;
};

export const sortMaintenanceRequirements = (requirements, maintenanceDueByRequirement) => {
  return [...requirements].sort((a, b) => {
    const aDue = maintenanceDueByRequirement[a.uri];
    const bDue = maintenanceDueByRequirement[b.uri];

    const aSeverity = SEVERITY_ORDER[aDue?.status_indicator] ?? 0;
    const bSeverity = SEVERITY_ORDER[bDue?.status_indicator] ?? 0;

    if (aSeverity !== bSeverity) {
      return bSeverity - aSeverity;
    }

    return getProgress(aDue) - getProgress(bDue);
  });
};
